import { useState, useEffect } from 'react'
import { useUploadVideoMutation } from '../../../../api/trainingTopics'
import useMutation from '../../../../hooks/useMutation'
import { isVideoFormatSupported, getVideoFormatName, getSupportedVideoFormatsText } from '../utils/videoUtils'

export function useVideoUploader({ url, onVideoChange, onVideoRemove, topicId }) {
  const [isHovering, setIsHovering] = useState(false)
  const [localUrl, setLocalUrl] = useState(url || '')
  const [error, setError] = useState('')

  const { submit: uploadVideo, isLoading } = useMutation(useUploadVideoMutation, {
    showSuccessToast: false
  })

  useEffect(() => {
    setLocalUrl(url || '')
  }, [url])

  const handleVideoUpload = async event => {
    const file = event.target.files[0]
    if (!file) return

    setError('')

    if (file.size > 450 * 1024 * 1024) {
      setError('File size exceeds 450MB limit')
      return
    }

    if (!isVideoFormatSupported(file)) {
      const userFormat = getVideoFormatName(file.type)
      const supportedFormats = getSupportedVideoFormatsText()
      console.warn('Unsupported file type:', file.type, 'for file:', file.name)
      setError(`Format ${userFormat} is not supported. Supported formats: ${supportedFormats}`)
      return
    }

    const formData = new FormData()
    formData.append('fileUpload', file)

    try {
      const response = await uploadVideo({
        topicId,
        body: formData
      })

      let responseUrl = null
      if (response?.videoUrl) {
        responseUrl = response.videoUrl
      } else if (response?.data?.videoUrl) {
        responseUrl = response.data.videoUrl
      } else if (response?.url) {
        responseUrl = response.url
      } else if (response?.data?.url) {
        responseUrl = response.data.url
      } else if (typeof response === 'string') {
        responseUrl = response
      }

      if (responseUrl) {
        setLocalUrl(responseUrl)
        onVideoChange(responseUrl)
      } else {
        throw new Error('Invalid response format: no video URL found')
      }
    } catch (error) {
      console.error('Error uploading video:', error)
      setError('Failed to upload video. Please try again.')
    }
  }

  const handleRemoveVideo = () => {
    setLocalUrl('')
    setError('')
    if (onVideoRemove) {
      onVideoRemove()
    }
  }

  const handleMouseEnter = () => setIsHovering(true)
  const handleMouseLeave = () => setIsHovering(false)

  return {
    localUrl,
    error,
    isLoading,
    isHovering,
    handleVideoUpload,
    handleRemoveVideo,
    handleMouseEnter,
    handleMouseLeave
  }
}
